// Header.js

import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Logo from "../../../Assets/logo.png";
import DownIcon from "../../../Assets/icons/Down-Black.svg";
import styles from "./Header.module.scss";

// Navigation data
const NAV_ITEMS = [
  { title: "Home", path: "/" },
  {
    title: "Shop Servers",
    path: "/all-dedicated-servers",
    submenu: [
      { title: "Standard Servers", path: "/standard-dedicated-servers" },
      { title: "Custom Servers", path: "/custom-dedicated-servers" },
      { title: "GPU Enabled Servers", path: "/gpu-enabled-servers" },
      { title: "Storage Servers", path: "/storage-servers" },
      { title: "Server Upgrades", path: "/server-upgrades" }
    ]
  },
  {
    title: "Data Centers",
    path: "/datacenter-locations-and-networks/utah",
    submenu: [
      { title: "Utah", path: "/datacenter-locations-and-networks/utah" },
      { title: "California", path: "/datacenter-locations-and-networks/california" },
      { title: "New York", path: "/datacenter-locations-and-networks/newyork" }
    ]
  },
  { title: "Colocation", path: "/colocation" },
  {
    title: "Company",
    path: "/about",
    submenu: [
      { title: "About", path: "/about" },
      { title: "Contact", path: "/contact" },
      { title: "Service Level Agreement", path: "/service-level-agreement" },
      { title: "Terms of Service", path: "/terms-of-service" }
    ]
  },
  { title: "Whats New?", path: "/blog", isNew: true }
];

const DropdownMenu = ({ items, isVisible, menuLabel }) => (
  <ul
    className={styles.dropdownMenu}
    role="menu"
    aria-label={`${menuLabel} Submenu`}
    data-visible={isVisible}
  >
    {items.map((item) => (
      <li key={item.path} role="menuitem">
        <a href={item.path} title={item.title}>
          {item.title}
        </a>
      </li>
    ))}
  </ul>
);

const NavItem = ({ item, hoveredMenu, setHoveredMenu }) => {
  const hasSubmenu = item.submenu?.length > 0;
  
  return (
    <li 
      className={styles.mainNavListItem}
      onMouseEnter={() => hasSubmenu && setHoveredMenu(item.title)}
      onMouseLeave={() => hasSubmenu && setHoveredMenu(null)}
    >
      <a
        href={item.path}
        className={`${styles.mainNavListItemLink} ${item.isNew ? styles.hasNewBadge : ''}`}
        title={item.title}
        onClick={(e) => {
          // Only prevent default if clicking the dropdown indicator
          if (e.target.tagName === 'IMG' || e.target.classList.contains(styles.dropdownIndicator)) {
            e.preventDefault();
          }
        }}
      >
        {item.title}
        {hasSubmenu && (
          <motion.img
            src={DownIcon}
            alt={`Expand ${item.title} Menu`}
            className={styles.dropdownIndicator}
            animate={{ rotate: hoveredMenu === item.title ? 180 : 0 }}
            transition={{ duration: 0.3 }}
          />
        )}
      </a>
      {hasSubmenu && (
        <DropdownMenu
          items={item.submenu}
          isVisible={hoveredMenu === item.title}
          menuLabel={item.title}
        />
      )}
    </li>
  );
};

const MobileNavItem = ({ item, hoveredMenu, setHoveredMenu }) => {
  const hasSubmenu = item.submenu?.length > 0;
  const isActive = hoveredMenu === item.title;

  if (!hasSubmenu) {
    return (
      <li>
        <a href={item.path} className={item.isNew ? styles.hasNewBadge : ''}>
          {item.title}
        </a>
      </li>
    );
  }

  return (
    <li>
      <div
        className={styles.mobileNavItemWithArrow}
        onClick={() => setHoveredMenu(isActive ? null : item.title)}
        role="button"
        aria-expanded={isActive}
      >
        <a href={item.path}>{item.title}</a>
        <motion.img
          src={DownIcon}
          alt={`Expand ${item.title} Menu`}
          className={`${styles.downArrow} ${isActive ? styles.rotated : ""}`}
          animate={{ rotate: isActive ? 180 : 0 }}
          transition={{ duration: 0.3 }}
        />
      </div>
      <AnimatePresence>
        {isActive && (
          <motion.ul
            className={styles.subMenuList}
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.3 }}
            role="menu"
          >
            {item.submenu.map((subItem) => (
              <li key={subItem.path} role="menuitem">
                <a href={subItem.path} title={subItem.title}>
                  {subItem.title}
                </a>
              </li>
            ))}
          </motion.ul>
        )}
      </AnimatePresence>
    </li>
  );
};

const Header = ({ isMobileMenuOpen, toggleMobileMenu, hasHero = true }) => {
  const [showHeader, setShowHeader] = useState(true);
  const [lastScrollY, setLastScrollY] = useState(0);
  const [hoveredMenu, setHoveredMenu] = useState(null);
  const [hasScrolled, setHasScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      if (currentScrollY === 0) {
        setShowHeader(true);
        setHasScrolled(false);
      } else {
        setShowHeader(currentScrollY < lastScrollY);
        setHasScrolled(true);
      }
      setLastScrollY(currentScrollY);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, [lastScrollY]);

  return (
    <div>
      <motion.div
        className={styles.fullWidthBackground}
        style={{
          boxShadow: hasHero && !hasScrolled
            ? "none"
            : "0px 4px 8px rgba(0, 0, 0, 0.1)"
        }}
        initial={{ y: 0 }}
        animate={{ y: showHeader ? 0 : "-100%" }}
        transition={{ duration: 0.3 }}
      >
        <div className={styles.container}>
          <header className={styles.desktopNav}>
            <a href="/" title="WebNX Home">
              <img src={Logo} alt="WebNX Company Logo" loading="eager" />
            </a>

            <nav className={styles.mainNav}>
              <ul className={styles.mainNavList}>
                {NAV_ITEMS.map((item) => (
                  <NavItem
                    key={item.path}
                    item={item}
                    hoveredMenu={hoveredMenu}
                    setHoveredMenu={setHoveredMenu}
                  />
                ))}
              </ul>
            </nav>

            <nav className={styles.nav2}>
              <ul className={styles.ul2}>
                <li>
                  <a href="https://clients.webnx.com/index.php?rp=/login" title="WebNX Client Login">
                    Login
                  </a>
                </li>
              </ul>
            </nav>

            <motion.button
              onClick={toggleMobileMenu}
              className={styles.hamburgerMenu}
              aria-label={isMobileMenuOpen ? "Close Navigation Menu" : "Open Navigation Menu"}
            >
              <motion.svg
                width="24"
                height="24"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <motion.path
                  d={isMobileMenuOpen ? "M6 6l12 12M6 18L18 6" : "M3 6h18M3 12h18M3 18h18"}
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </motion.svg>
            </motion.button>
          </header>

          <AnimatePresence>
            {isMobileMenuOpen && (
              <motion.nav
                className={styles.mobileNav}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3 }}
              >
                <ul className={styles.mobileNavList}>
                  {NAV_ITEMS.map((item) => (
                    <MobileNavItem
                      key={item.path}
                      item={item}
                      hoveredMenu={hoveredMenu}
                      setHoveredMenu={setHoveredMenu}
                    />
                  ))}
                  <li>
                    <a href="https://clients.webnx.com/index.php?rp=/login" title="Client Login">
                      Client Login
                    </a>
                  </li>
                </ul>
              </motion.nav>
            )}
          </AnimatePresence>
        </div>
      </motion.div>
    </div>
  );
};

export default Header;
